import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';

import {
    ExtensionRequest,
    IProjectRegistrationModel,
    ProjectModel,
} from '../../../store/types';
import {
    ExtensionRequestState,
    InvitationStatus,
    ProjectSecondSupervisorStatus,
    ProjectStatus,
} from '../../../utils/utils';
import { QuestionCircleIcon, CircleWithArrow } from '../../shared/icons/icons';
import { ProjectProgressBar } from '../../shared/ProjectProgressBar/ProjectProgressBar';
import { RootState } from '../../../store';
import SusyAvatar, {
    SuSyAvatarSizes,
} from '../../shared/susyAvatar/SusyAvatar';
import { isWaitingForAdminProjectRegistrationApproval } from '../../../utils/projectCalculations';
import ExtensionRequestBadge from '../../shared/ExtensionRequestStatusBadge';

import {
    DateFormat,
    formatDate,
    formatTimeWithLocaleTimeString,
} from 'susy-common/date-utils';

import './ProjectItem.css';

interface IProps {
    linkTo: string;
    project: ProjectModel;
}

const ProjectCard = (props: IProps) => {
    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.project.projectItem
    );
    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );

    const getDeadlineStyle = (date: Date | undefined | null) => {
        let classStyleDone = '';
        if (date) {
            classStyleDone = ' thesisOverview-card-deadline-done';
        }
        return 'thesisOverview-card-deadline' + classStyleDone;
    };

    const submissionDeadline = (): { date: string; time: string } => {
        const projectTimeline = props.project?.projectTimeline;
        const submissionDeadline =
            props.project.dates?.deadlineDates?.submission;

        if (projectTimeline && submissionDeadline) {
            return {
                date: formatDate(
                    submissionDeadline,
                    DateFormat.MMM_DD_COMMA_YYYY
                ),
                time: formatTimeWithLocaleTimeString(submissionDeadline),
            };
        }

        return { date: '', time: '' };
    };

    const registrationDeadline = (): { date: string; time: string } => {
        const registrationDeadline =
            props.project.projectTimeline?.registrationDeadlineDate;

        if (registrationDeadline) {
            return {
                date: formatDate(
                    registrationDeadline,
                    DateFormat.MMM_DD_COMMA_YYYY
                ),
                time: formatTimeWithLocaleTimeString(registrationDeadline),
            };
        }

        return { date: '', time: '' };
    };

    const getApprovedProjectNextStepString = (
        projectStatus: ProjectStatus,
        secondSupervisorStatus: ProjectSecondSupervisorStatus | null,
        projectRegistration: IProjectRegistrationModel | undefined
    ): string => {
        let retVal = '';

        if (
            isWaitingForAdminProjectRegistrationApproval(
                projectStatus,
                projectRegistration
            )
        ) {
            retVal = localization.pendingProjectRegistration;
        } else {
            switch (secondSupervisorStatus) {
                case ProjectSecondSupervisorStatus.pending: {
                    retVal = localization.secondSupervisorPending;
                    break;
                }
                case ProjectSecondSupervisorStatus.accepted: {
                    retVal = localization.secondSupervisorAccepted;
                    break;
                }
                default: {
                    retVal = localization.noSecondSupervisor;
                    break;
                }
            }
        }

        return retVal;
    };

    const getProjectNextStepString = (
        projectStatus: ProjectStatus,
        firstSupervisorAdded: boolean,
        invitationStatus: InvitationStatus | null,
        secondSupervisorStatus: ProjectSecondSupervisorStatus | null,
        projectRegistration: IProjectRegistrationModel | undefined
    ): string => {
        switch (projectStatus.toString()) {
            case ProjectStatus.withdrawn:
            case ProjectStatus.not_initialized:
            case ProjectStatus.declined:
                return localization.projectDeclined;
            case ProjectStatus.saved:
                return localization.projectSaved;
            case ProjectStatus.pending:
                return invitationStatus === InvitationStatus.pending
                    ? localization.pendingInvitationStatus
                    : firstSupervisorAdded
                    ? localization.notPendingInvitationStatus
                    : localization.noFirstSupervisor;
            case ProjectStatus.approved:
                return getApprovedProjectNextStepString(
                    projectStatus,
                    secondSupervisorStatus,
                    projectRegistration
                );
            case ProjectStatus.registered:
                return localization.projectRegistered;
            case ProjectStatus.handedIn:
                return localization.projectHandedIn;
            case ProjectStatus.evaluated:
                return localization.projectEvaluated;
            case ProjectStatus.failed:
                return localization.projectFailed;
            default:
                return '';
        }
    };

    const nextStep = (): string => {
        const myMember = props.project.teamList?.find((member) => {
            return member?.member?._id === currentUser?._id;
        });
        const secondSupervisorStatus =
            props.project.secondSupervisorProposals.every(
                (ssp) => ssp.status === ProjectSecondSupervisorStatus.accepted
            ) &&
            props.project.secondSupervisorProposals.length ===
                props.project.projectTypeSettings.supervisorSettings
                    .numberOfRequiredSupervisors -
                    1
                ? ProjectSecondSupervisorStatus.accepted
                : props.project.secondSupervisorProposals.some(
                      (ssp) =>
                          ssp.status === ProjectSecondSupervisorStatus.pending
                  )
                ? ProjectSecondSupervisorStatus.pending
                : null;
        if (myMember) {
            return getProjectNextStepString(
                props.project.currentStatus,
                !!props.project.supervisor,
                myMember.invitationStatus,
                secondSupervisorStatus,
                props.project.projectRegistration
            );
        }
        return getProjectNextStepString(
            props.project.currentStatus,
            !!props.project.supervisor,
            null,
            secondSupervisorStatus,
            props.project.projectRegistration
        );
    };

    const getExtensionRequestBadge = (project: ProjectModel): ReactNode => {
        if (
            !project.extensionRequests ||
            project.extensionRequests?.length === 0
        ) {
            return null;
        }

        const lastCreatedExtensionRequest =
            project.extensionRequests[project.extensionRequests.length - 1];

        const shouldDisplayExtensionRequestOnProjectDashboardCard = (
            project: ProjectModel,
            lastExtensionRequest: ExtensionRequest
        ) => {
            const lastExtReqHasReceivedAnswerFromAdminOrSupervisor =
                lastExtensionRequest.state === ExtensionRequestState.Accepted ||
                lastExtensionRequest.state === ExtensionRequestState.Declined;

            if (!lastExtReqHasReceivedAnswerFromAdminOrSupervisor) {
                return true;
            }

            return someUsersHaventSeenAndConfirmedExtReq(
                project,
                lastExtensionRequest
            );
        };

        const someUsersHaventSeenAndConfirmedExtReq = (
            project: ProjectModel,
            lastExtensionRequest: ExtensionRequest
        ) => {
            const userIdsWhichConfirmedLastExtReq =
                lastExtensionRequest.answerConfirmedByUsers?.map(
                    (user) => user._id
                );

            const ownerAndMember = [
                ...project.teamList.map((member) => member.member),
                project.proposedBy,
            ];

            return ownerAndMember?.some((user) => {
                return !userIdsWhichConfirmedLastExtReq?.includes(user._id);
            });
        };

        return (
            shouldDisplayExtensionRequestOnProjectDashboardCard(
                project,
                lastCreatedExtensionRequest
            ) && (
                <div>
                    <ExtensionRequestBadge
                        extensionRequest={lastCreatedExtensionRequest}
                    />
                </div>
            )
        );
    };

    const supervisor = props.project?.supervisor;
    const registrationDeadlineStruct = registrationDeadline();
    const submissionDeadlineStruct = submissionDeadline();

    return (
        <div className='col-12 col-sm-6 col-xl-4 col-xxl-3 col-fill'>
            <div className='card thesisOverview-card'>
                <Link
                    to={`${props.linkTo}`}
                    className='thesisOverview-card-link'
                >
                    {/* Body */}
                    <div className='card-body'>
                        <div className='pb-3'>
                            <ProjectProgressBar
                                currentStatus={props.project?.currentStatus}
                            />
                        </div>

                        {/* Project title */}
                        <div className='thesisOverview-card-title mb-0'>
                            <strong>{props.project?.title}</strong>
                        </div>

                        <div className='thesisOverview-card-icon-section'>
                            {/* Next step  */}
                            <div className='thesisOverview-card-next-step'>
                                <div className='thesisOverview-card-next-step-title'>
                                    <QuestionCircleIcon />
                                    {localization.nextStep}
                                </div>
                                <div>{nextStep()}</div>
                            </div>

                            {/* Research Area  */}
                            {props.project?.researchArea?.title && (
                                <div className='thesisOverview-card-research-area'>
                                    <div className='thesisOverview-card-research-area-title'>
                                        <CircleWithArrow />
                                        {localization.researchArea}
                                    </div>
                                    <div>
                                        {props.project?.researchArea?.title}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Footer */}
                    <div className='card-footer d-flex'>
                        {/* Supervisor data */}
                        <div className='thesisOverview-card-supervisor d-flex justify-content-between align-items-center'>
                            <div className='d-flex flex align-items-center'>
                                <SusyAvatar
                                    size={SuSyAvatarSizes.MD}
                                    profilePictureId={
                                        props.project?.supervisor
                                            ?.profilePictureId
                                    }
                                />

                                <div>
                                    <div className='supervisor-name'>
                                        {supervisor?.firstName}{' '}
                                        {supervisor?.lastName}
                                    </div>
                                    <span>{localization.supervisor}</span>
                                </div>
                            </div>
                            <div className='p-2'>
                                {getExtensionRequestBadge(props.project)}
                            </div>
                        </div>
                    </div>
                    <div className='card-footer d-flex'>
                        {/* Registration Deadline */}
                        <div
                            className={`${getDeadlineStyle(
                                props.project?.dates?.registered
                            )}`}
                        >
                            <div className='d-flex flex-column susy-min-width-90'>
                                <span className='text-muted'>
                                    {localization.registrationDeadline}
                                </span>
                                <span className='susy-strong-text'>
                                    {registrationDeadlineStruct.date}
                                </span>
                                <span className='susy-strong-text'>
                                    {registrationDeadlineStruct.time}
                                </span>
                            </div>
                        </div>

                        {/* Submission deadline */}
                        <div
                            className={`${getDeadlineStyle(
                                props.project?.dates?.handedIn
                            )}`}
                        >
                            <div className='d-flex flex-column susy-min-width-90'>
                                <span className='text-muted'>
                                    {localization.submissionDeadline}
                                </span>
                                <span className='susy-strong-text'>
                                    {submissionDeadlineStruct.date}
                                </span>
                                <span className='susy-strong-text'>
                                    {submissionDeadlineStruct.time}
                                </span>
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    localization: state.localization,
});

export default connect(mapStateToProps)(ProjectCard);
